import {
  CustomerCageWithAnimalsDetailsDto,
  CustomerDto,
  CustomerSubscriptionDtoPlan,
  InitiateCustomerDto,
} from '../gen/backendClient'
import { encryptPassword } from '../../utils/encryptPassword'
import { backendClient } from './client'

import { AxiosRequestConfig } from 'axios'

/**
 * The generated client still marks SIRETNumber as required: it is not regenerated for this endpoint
 * (same as the guest adoption ones). An organization holds a SIRET or an RNA, so the backend takes
 * either and stores it in its own field.
 */
export type InitiateCustomerBody = Omit<InitiateCustomerDto, 'customer'> & {
  customer: Omit<InitiateCustomerDto['customer'], 'SIRETNumber'> & {
    SIRETNumber?: string
    RNANumber?: string
  }
}

export class CustomersClient {
  public static async createCustomerSpace(params: InitiateCustomerBody): Promise<CustomerDto> {
    const config: AxiosRequestConfig<InitiateCustomerBody> = {
      method: 'POST',
      url: '/customers',
      data: {
        ...params,
        adminAccounts: params.adminAccounts.map((account) => ({
          ...account,
          password: encryptPassword(account.password),
        })),
      },
    }

    const response = await backendClient<CustomerDto>(config)

    return response.data
  }

  public static async getCurrentCustomer(): Promise<CustomerDto> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: '/customers',
    }

    const response = await backendClient<CustomerDto>(config)

    return response.data
  }

  public static async editCustomer(params: CustomerDto): Promise<CustomerDto> {
    const config: AxiosRequestConfig<CustomerDto> = {
      method: 'PUT',
      url: '/customers',
      data: {
        ...params,
      },
    }

    const response = await backendClient<CustomerDto>(config)

    return response.data
  }

  public static async getCurrentCustomerCagesWithAnimalsNames(): Promise<CustomerCageWithAnimalsDetailsDto[]> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: '/customers/cages',
    }

    const response = await backendClient<CustomerCageWithAnimalsDetailsDto[]>(config)

    return response.data
  }

  public static async getCustomerSubscription(): Promise<CustomerSubscriptionDtoPlan> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: '/customers/get-subscription',
    }

    const response = await backendClient<CustomerSubscriptionDtoPlan>(config)

    return response.data
  }

  public static async getCustomerAppLockedDate(): Promise<string | null> {
    const config: AxiosRequestConfig = {
      method: 'GET',
      url: '/customers/get-locked-date',
    }

    const response = await backendClient<string | null>(config)

    return response.data
  }

  public static async requestFullAssociationExportJob(): Promise<{ jobId: string; alreadyRunning: boolean }> {
    const config: AxiosRequestConfig = {
      method: 'POST',
      url: '/customers/export/full/jobs',
    }

    const response = await backendClient<{ jobId: string; alreadyRunning: boolean }>(config)

    return response.data
  }

  public static async uploadProfileImage(image: File): Promise<void> {
    const formData = new FormData()

    formData.append('file', image)

    const config: AxiosRequestConfig<FormData> = {
      method: 'POST',
      url: `/customers/upload-profile-photos`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
        Accept: 'image/',
      },
    }

    await backendClient<void>(config)
  }

  public static async setSignatureFile(signature: File): Promise<void> {
    const formData = new FormData()

    formData.append('file', signature)

    const config: AxiosRequestConfig<FormData> = {
      method: 'POST',
      url: `/customers/signature`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }

    await backendClient<void>(config)
  }
}
